import React from 'react';
import { AdvancedFiltersPanel } from './AdvancedFiltersPanel';

export interface SortOption {
  value: string;
  label: string;
}

interface ListToolbarProps {
  searchValue: string;
  onSearchChange: (v: string) => void;
  searchPlaceholder?: string;
  sortValue?: string;
  sortOptions?: SortOption[];
  onSortChange?: (v: string) => void;
  onClearFilters?: () => void;
  showClear?: boolean;
  /** Inline quick filter (always visible per LP-UX-001). */
  quickFilter?: React.ReactNode;
  /** Rarely-used filters behind disclosure panel. */
  advancedFilters?: React.ReactNode;
  advancedFilterCount?: number;
  /** Legacy slot — prefer quickFilter + advancedFilters. */
  children?: React.ReactNode;
}

export const ListToolbar: React.FC<ListToolbarProps> = ({
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search…',
  sortValue,
  sortOptions,
  onSortChange,
  onClearFilters,
  showClear,
  quickFilter,
  advancedFilters,
  advancedFilterCount = 0,
  children,
}) => (
  <div className="flex flex-col gap-3 rounded-xl border border-border bg-surface p-4 shadow-sm">
    <div className="relative w-full sm:max-w-lg rounded-xl ring-1 ring-border focus-within:ring-2 focus-within:ring-primary">
      <svg
        className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-fg-muted"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
      </svg>
      <input
        type="search"
        value={searchValue}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={searchPlaceholder}
        className="input-field w-full rounded-xl border-0 bg-input py-2.5 pl-10 pr-3 text-base text-fg placeholder:text-fg-muted focus:outline-none"
      />
    </div>

    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
      {sortOptions && sortOptions.length > 0 && onSortChange ? (
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-fg-muted">Sort</label>
          <select
            value={sortValue ?? sortOptions[0]?.value}
            onChange={(e) => onSortChange(e.target.value)}
            className="input-field rounded-xl border border-border bg-input px-3 py-2 text-fg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {sortOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      <div className="flex flex-wrap items-end gap-2">
        {quickFilter}
        {advancedFilters ? (
          <AdvancedFiltersPanel activeCount={advancedFilterCount}>{advancedFilters}</AdvancedFiltersPanel>
        ) : null}
        {children}
      </div>
      {showClear && onClearFilters ? (
        <button type="button" onClick={onClearFilters} className="btn-surface-muted rounded-xl px-3 py-2 text-sm">
          Clear filters
        </button>
      ) : null}
    </div>
  </div>
);
